"use client";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import toast from "react-hot-toast";
import {
  useResendOTPMutation,
  useVerifyOTPMutation,
} from "../../apis/loginApis";
import { useNavigate } from "react-router-dom";
import { useProfileData } from "../../apis/profileApis";
import { useDispatch } from "react-redux";
import { addProfileData } from "../../features/auth/authSlice";
type Props = {
  email: string;
};

interface LoginForm extends HTMLFormElement {
  readonly elements: HTMLFormControlsCollection;
}

const OtpForm = (props: Props) => {
  const { t } = useTranslation();
  const [otpDigits, setOtpDigits] = useState<string[]>(new Array(6).fill(""));
  const [resendEnabled, setResendEnabled] = useState(false);
  const dispatch = useDispatch();
  const [timeLeft, setTimeLeft] = useState(30);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (timeLeft > 0 && !resendEnabled) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setResendEnabled(true);
    }
    return () => clearInterval(timer);
  }, [timeLeft, resendEnabled]);

  const { mutate, isPending } = useVerifyOTPMutation();
  const { mutate: resendMutate } = useResendOTPMutation();
  const { refetch } = useProfileData();
  const handleSubmit = async (event: React.FormEvent<LoginForm>) => {
    event.preventDefault();
    const otpCode = otpDigits.join("");

    if (otpCode.length !== 6) {
      toast.error("Please enter complete 6-digit OTP");
    }

    mutate(
      { otp: otpCode, email: props.email },
      {
        onSuccess: async (data) => {
          const profileResponse = await refetch();
          if (profileResponse?.data) {
            dispatch(addProfileData(profileResponse.data));
            if(!profileResponse?.data?.isWarehouseCreated && profileResponse?.data?.clientType !== 'DS'){
                navigate("/create-warehouse")
              }else{
                navigate("/dashboard");
              }
          } else {
            toast.error("Failed to load user profile.");
          }
        },
        onError: (error) => {
          toast.error(`Login failed: ${error}`);
        },
      }
    );
  };

  const handleResendOtp = async () => {
    try {
      setResendEnabled(false);
      resendMutate(
        { email: props.email },
        {
          onSuccess: (data) => {
            console.log("Logged in:", data);
          },
          onError: (error) => {
            console.log(error, "error");
            toast.error(`Login failed: ${error}`);
          },
        }
      );
      setTimeLeft(30);
    } catch (error) {
      console.error("Resend OTP error:", error);
      setResendEnabled(true);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = value.slice(-1);
    setOtpDigits(newOtpDigits);

    if (value && index < 5) {
      document.getElementById(`otp-input-${index + 1}`)?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`)?.focus();
    }
  };

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      return alert("Passwords do not match");
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("Text").trim();

    if (!/^\d+$/.test(pastedData)) return; // Ensure only numbers

    const digits = pastedData.slice(0, otpDigits.length).split("");

    const newOtp = [...otpDigits];
    digits.forEach((digit, i) => {
      newOtp[i] = digit;
    });

    setOtpDigits(newOtp);

    // Optionally move focus to the last filled input
    const nextIndex = digits.length - 1;
    const nextInput = document.getElementById(`otp-input-${nextIndex}`);
    if (nextInput) nextInput.focus();
  };

  return (
    <div className="flex justify-center items-center w-full h-full">
      <div className="text-center rounded-2xl  lg:p-8 p-6 space-y-6">
        <div className="space-y-1">
          <h2 className="font-semibold text-3xl tracking-tight">
            {!isOtpVerified ? t("OTP Verification") : t("Change Password")}
          </h2>
          <p className="text-base text-black/70">
            {!isOtpVerified
              ? `${t("We've sent a 6-digit OTP to")} ${props?.email}`
              : `${t("Please Set Strong Password")} ${props?.email}`}
          </p>
        </div>

        <div>
          {!isOtpVerified ? (
            <form className="space-y-4" id="loginForm" onSubmit={handleSubmit}>
              <div className="text-left space-y-1">
                <div className="flex items-baseline justify-between">
                  <label className="FormLabel" aria-required="true">
                    {t("OTP (6-digit)")}
                    <span className="text-[#DC3545] font-bold">*</span>
                  </label>
                </div>

                <div className="flex gap-2 justify-center w-full">
                  {otpDigits.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-input-${index}`}
                      className="w-16 h-12 Input bg-white border text-center border-gray-300 block rounded-md focus:border-[#FBD037] focus:outline-none"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={(e) => handleOtpPaste(e)}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>

                <div className="text-right pt-2">
                  {resendEnabled ? (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="text-[#434242] hover:underline font-medium"
                    >
                      {t("Resend OTP")}
                    </button>
                  ) : (
                    <p className="text-gray-500 text-sm">
                      {t("Resend OTP in")} {timeLeft}s
                    </p>
                  )}
                </div>
              </div>
              <button
                type="submit"
                disabled={isPending} // disable when loading
                className={`w-full flex justify-center items-center gap-2 font-semibold py-3 rounded-full transition
    ${
      isPending
        ? "bg-gray-800 cursor-not-allowed text-[#ffffff]"
        : "bg-[#FBD037] hover:bg-yellow-400 text-[#000000]"
    }
    `}
              >
                {isPending ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16z"
                      />
                    </svg>
                    Verifying...
                  </>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="text-left space-y-2">
                <label>
                  {t("New Password")}
                  <span className="text-[#DC3545] font-bold">*</span>
                </label>
                <div className="relative">
                  <input
                    className="Input bg-white border border-gray-300 w-full block px-4 py-2 rounded-md focus:border-[#FBD037] focus:outline-none pr-10"
                    type={showPassword ? "text" : "password"}
                    placeholder="*******"
                    required
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <span
                    className="absolute top-2/4 right-3 transform -translate-y-1/2 cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <IconEyeOff size={20} />
                    ) : (
                      <IconEye size={20} />
                    )}
                  </span>
                </div>
              </div>

              <div className="text-left space-y-2">
                <label>
                  {t("Confirm Password")}
                  <span className="text-[#DC3545] font-bold">*</span>
                </label>
                <div className="relative">
                  <input
                    className="Input bg-white border border-gray-300 w-full block px-4 py-2 rounded-md focus:border-[#FBD037] focus:outline-none pr-10"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="*******"
                    required
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <span
                    className="absolute top-2/4 right-3 transform -translate-y-1/2 cursor-pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <IconEyeOff size={20} />
                    ) : (
                      <IconEye size={20} />
                    )}
                  </span>
                </div>
              </div>

              <button
                type="submit"
                className="border border-[#FBD037] bg-[#FBD037] rounded-md p-2 px-4 w-full font-medium cursor-pointer"
              >
                {t("Change Password")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtpForm;
